import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Checkbox,
  TextField,
  Autocomplete,
  Typography,
  Tooltip,
  Chip,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

import {
  getJobCodeMappings,
  createJobCodeMapping,
  updateJobCodeMapping,
  deleteJobCodeMapping,
  bulkUpdateJobCodeMappings,
  getPrograms,
  getCountries,
  getCoreRatioBuckets,
  getDepartments,
  getJobCodes,
} from "services/userServices";
import BackdropComponent from "components/Spinner/Spinner";
import SnackBar from "components/SnackBar/SnackBar";
import { useDispatch } from "react-redux";
import { SnackBarStoreAction } from "Store/SnackBar";

const JobCodeMapping = () => {
  // Custom font styles
  const fontStyles = {
    fontFamily: "Urbanist, Verdana, Arial, sans-serif",
    letterSpacing: "0.10em",
    color: "#3F5765",
  };

  const headerFontStyles = {
    ...fontStyles,
    fontWeight: "bold",
  };

  // State Management
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showInactive, setShowInactive] = useState(false);
  const [highlightedCells, setHighlightedCells] = useState(new Set());

  // Sorting State
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");

  // Dialog State
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Menu State
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRecord, setMenuRecord] = useState(null);

  const dispatch = useDispatch();
  const showSnack = (message, severity) => {
    const obj = {
      open: true,
      message: message,
      severity: severity,
    };
    dispatch(SnackBarStoreAction.updateSnack(obj));
  };

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  // Confirmation Dialog State
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
    confirmColor: "primary",
  });

  // Dropdown Options State
  const [programs, setPrograms] = useState([]);
  const [countries, setCountries] = useState([]);
  const [coreRatioBuckets, setCoreRatioBuckets] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [jobCodes, setJobCodes] = useState([]);

  // Bulk Edit Header Values
  const [headerFilters, setHeaderFilters] = useState({
    program: null,
    country: null,
    jobCode: null,
    department: null,
    coreRatioBucket: null,
    startDate: null,
    endDate: null,
  });

  // Form State
  const [formData, setFormData] = useState({
    coreRatioCountryJobCodeMapId: null,
    program: null,
    programCode: "",
    country: null,
    countryCode: "",
    jobCode: null,
    jobCodeValue: "",
    department: null,
    departmentCode: "",
    departmentNumber: "",
    coreRatioBucket: null,
    startDate: null,
    endDate: null,
  });

  // Load Initial Data
  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      try {
        await loadInitialData();
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load initial data:", error);
        }
      }
    };

    initialize();

    return () => {
      cancelled = true;
    };
  }, []);

  // Reload data when showInactive changes
  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      try {
        await loadTableData();
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load table data:", error);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [showInactive]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadTableData(), loadDropdownOptions()]);
    } catch (error) {
      console.error("Error loading initial data:", error);
      showSnackbar("Failed to load initial data", "error");
    } finally {
      setLoading(false);
    }
  };

  // API Calls
  const loadTableData = async () => {
    setLoading(true);
    try {
      const response = await getJobCodeMappings(
        null, // programCode
        null, // countryCode
        null, // jobCode
        null, // departmentCode
        0, // coreRatioBucketId
        showInactive, // includeInactive
      );

      if (response.data.status && response.data.data) {
        if (Array.isArray(response.data.data)) {
          const transformedData = response.data.data.map((item) => ({
            coreRatioCountryJobCodeMapId: item.CoreRatioCountryJobCodeMapId,
            program: {
              name: item.ProgramName,
              code: item.ProgramCode,
            },
            country: {
              name: item.CountryName || item.CountryCode,
              code: item.CountryCode,
            },
            jobCode: {
              code: item.JobCode,
              description: item.JobCodeDescription || item.JobCode,
            },
            department: {
              name: item.DepartmentName,
              code: item.DepartmentCode,
              number: item.DepartmentNumber,
            },
            coreRatioBucket: {
              id: item.CoreRatioBucketId,
              name: item.CoreRatioBucketName,
            },
            startDate: item.MappingStartDate
              ? new Date(item.MappingStartDate)
              : null,
            endDate: item.MappingEndDate ? new Date(item.MappingEndDate) : null,
          }));

          setTableData(transformedData);
        } else {
          console.warn(
            "API returned data but it is not an array:",
            response.data.data,
          );
          setTableData([]);
          showSnackbar("No data available", "info");
        }
      } else {
        console.warn("API returned no data or status is false");
        setTableData([]);
        showSnackbar(response.data.message || "No data available", "info");
      }
    } catch (error) {
      console.error("Error loading data:", error);
      setTableData([]);
      showSnackbar("Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to process programs response
  const processProgramsResponse = (response, setPrograms) => {
    if (response.data.status && Array.isArray(response.data.data)) {
      setPrograms(
        response.data.data.map((p) => ({
          name: p.ProgramName,
          code: p.ProgramCode,
        })),
      );
    } else {
      setPrograms([]);
    }
  };

  // Helper function to process countries response
  const processCountriesResponse = (response, setCountries) => {
    if (response.data.status && Array.isArray(response.data.data)) {
      setCountries(
        response.data.data.map((c) => ({
          name: c.CountryName,
          code: c.CountryCode,
        })),
      );
    } else {
      setCountries([]);
    }
  };

  // Helper function to process buckets response
  const processBucketsResponse = (response, setCoreRatioBuckets) => {
    if (response.data.status && Array.isArray(response.data.data)) {
      setCoreRatioBuckets(
        response.data.data.map((b) => ({
          id: b.CoreRatioBucketId,
          name: b.CoreRatioBucketName,
        })),
      );
    } else {
      setCoreRatioBuckets([]);
    }
  };

  // Helper function to process departments response
  const processDepartmentsResponse = (response, setDepartments) => {
    if (response.data.status && Array.isArray(response.data.data)) {
      setDepartments(
        response.data.data.map((d) => ({
          name: d.DepartmentName,
          code: d.DepartmentCode,
          number: d.DepartmentNumber,
        })),
      );
    } else {
      setDepartments([]);
    }
  };

  // Helper function to process job codes response
  const processJobCodesResponse = (response, setJobCodes) => {
    if (response.data.status && Array.isArray(response.data.data)) {
      setJobCodes(
        response.data.data.map((jc) => ({
          code: jc.JobCode,
          description: jc.JobCodeDescription || jc.JobCode,
        })),
      );
    } else {
      setJobCodes([]);
    }
  };

  // Refactored main function
  const loadDropdownOptions = async () => {
    try {
      const [
        programsRes,
        countriesRes,
        bucketsRes,
        departmentsRes,
        jobCodesRes,
      ] = await Promise.all([
        getPrograms(),
        getCountries(),
        getCoreRatioBuckets(),
        getDepartments(),
        getJobCodes(),
      ]);

      processProgramsResponse(programsRes, setPrograms);
      processCountriesResponse(countriesRes, setCountries);
      processBucketsResponse(bucketsRes, setCoreRatioBuckets);
      processDepartmentsResponse(departmentsRes, setDepartments);
      processJobCodesResponse(jobCodesRes, setJobCodes);
    } catch (error) {
      console.error("Error loading dropdown options:", error);
      showSnackbar("Failed to load dropdown options", "error");
    }
  };

  // Utility Functions
  const showSnackbar = (message, severity = "success") => {
    showSnack(message, severity);
  };

  const formatDate = (date) => {
    if (!date) return "";
    const day = date.getDate();
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const formatDateForSQL = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const validateDates = (startDate, endDate) => {
    if (startDate && endDate) {
      if (endDate < startDate) {
        return "End date cannot be earlier than start date";
      }
    }
    return null;
  };

  // Helper function to create cell key for highlighting
  const getCellKey = (rowId, field) => `${rowId}-${field}`;

  // Helper function to check if cell is highlighted
  const isCellHighlighted = (rowId, field) => {
    return highlightedCells.has(getCellKey(rowId, field));
  };

  // Helper function to get highlighted cell styles
  const getHighlightedCellStyles = (rowId, field) => {
    if (isCellHighlighted(rowId, field)) {
      return {
        ...fontStyles,
        backgroundColor: "#fff4e6",
        transition: "background-color 0.3s ease",
      };
    }
    return fontStyles;
  };

  // Confirmation Dialog Handlers
  const showConfirmDialog = (
    title,
    message,
    onConfirm,
    confirmColor = "primary",
  ) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      onConfirm,
      confirmColor,
    });
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialog({
      open: false,
      title: "",
      message: "",
      onConfirm: null,
      confirmColor: "primary",
    });
  };

  const handleConfirmDialogAccept = () => {
    if (confirmDialog.onConfirm) {
      confirmDialog.onConfirm();
    }
    handleConfirmDialogClose();
  };

  // Selection Handlers
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelected = tableData.map((n) => n.coreRatioCountryJobCodeMapId);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleSelectRow = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Row click handler
  const handleRowClick = (event, id) => {
    const target = event.target;
    const isInteractive =
      target.closest("button") ||
      target.closest('input[type="checkbox"]') ||
      target.closest(".MuiAutocomplete-root") ||
      target.closest(".MuiTextField-root");

    if (!isInteractive) {
      handleSelectRow(id);
    }
  };

  // Pagination Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Sorting Handlers
  const handleSort = (column) => {
    const isAsc = orderBy === column && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(column);
  };

  const getSortValue = (row, column) => {
    switch (column) {
      case "program":
        return row.program?.name?.toLowerCase() || "";
      case "programCode":
        return row.program?.code?.toLowerCase() || "";
      case "country":
        return row.country?.name?.toLowerCase() || "";
      case "countryCode":
        return row.country?.code?.toLowerCase() || "";
      case "jobCode":
        return row.jobCode?.code?.toLowerCase() || "";
      case "department":
        return row.department?.name?.toLowerCase() || "";
      case "departmentNumber":
        return row.department?.number || "";
      case "coreRatioBucket":
        return row.coreRatioBucket?.name?.toLowerCase() || "";
      case "startDate":
        return row.startDate?.getTime() || 0;
      case "endDate":
        return row.endDate?.getTime() || 0;
      default:
        return "";
    }
  };

  const getSortedData = () => {
    if (!orderBy) return tableData;

    return [...tableData].sort((a, b) => {
      const aValue = getSortValue(a, orderBy);
      const bValue = getSortValue(b, orderBy);

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
  };

  const paginatedData = getSortedData().slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // Bulk Edit Functions
  // Helper function to validate bulk edit selection
  const validateBulkSelection = (
    selected,
    showSnackbar,
    setHeaderFilters,
    field,
  ) => {
    if (selected.length === 0) {
      showSnackbar("Please select rows to apply bulk edit", "warning");
      setHeaderFilters((prev) => ({ ...prev, [field]: null }));
      return false;
    }
    return true;
  };

  // Helper function to validate dates for bulk edit
  const validateBulkDates = (
    field,
    value,
    selected,
    tableData,
    validateDates,
    showSnackbar,
    setHeaderFilters,
  ) => {
    if (field !== "startDate" && field !== "endDate") {
      return true;
    }

    const selectedRows = tableData.filter((row) =>
      selected.includes(row.coreRatioCountryJobCodeMapId),
    );

    for (const row of selectedRows) {
      const startDate = field === "startDate" ? value : row.startDate;
      const endDate = field === "endDate" ? value : row.endDate;

      const dateError = validateDates(startDate, endDate);
      if (dateError) {
        showSnackbar(
          `${dateError} for ${row.program?.name} (${row.country?.name})`,
          "error",
        );
        setHeaderFilters((prev) => ({ ...prev, [field]: null }));
        return false;
      }
    }

    return true;
  };

  // Helper function to get update value for bulk edit
  const getBulkUpdateValue = (field, value, formatDateForSQL) => {
    if (field === "startDate" || field === "endDate") {
      return formatDateForSQL(value);
    }

    // Handle object fields with safe access
    const fieldValueMap = {
      program: value?.code || "",
      country: value?.code || "",
      jobCode: value?.code || "",
      department: value?.code || "",
      coreRatioBucket: value?.id?.toString() || "",
    };

    const mappedValue = fieldValueMap[field];
    const finalValue = mappedValue !== undefined ? mappedValue : value;

    return finalValue;
  };

  // Helper function to perform bulk update API call
  const performBulkUpdateAPI = async (
    field,
    value,
    selected,
    formatDateForSQL,
    bulkUpdateJobCodeMappings,
  ) => {
    const fieldMapping = {
      program: "ProgramCode",
      country: "CountryCode",
      jobCode: "JobCode",
      department: "DepartmentCode",
      coreRatioBucket: "CoreRatioBucketId",
      startDate: "StartDate",
      endDate: "EndDate",
    };

    const updateValue = getBulkUpdateValue(field, value, formatDateForSQL);

    const bulkUpdatePayload = {
      mappingIds: selected.join(","),
      updateField: fieldMapping[field],
      updateValue: updateValue,
    };

    return await bulkUpdateJobCodeMappings(bulkUpdatePayload);
  };

  // Helper function to handle successful bulk update
  const handleSuccessfulBulkUpdate = (
    selected,
    field,
    showSnackbar,
    setHighlightedCells,
    getCellKey,
    loadTableData,
    setSelected,
    setHeaderFilters,
  ) => {
    showSnackbar(`Updated ${selected.length} rows`, "success");

    // Highlight the updated cells
    const newHighlightedCells = new Set();
    selected.forEach((rowId) => {
      newHighlightedCells.add(getCellKey(rowId, field));
    });
    setHighlightedCells(newHighlightedCells);

    // Clear highlighting after 3 seconds
    setTimeout(() => {
      setHighlightedCells(new Set());
    }, 3000);

    loadTableData();
    setSelected([]);

    setHeaderFilters({
      program: null,
      country: null,
      jobCode: null,
      department: null,
      coreRatioBucket: null,
      startDate: null,
      endDate: null,
    });
  };

  // Refactored main function
  const applyBulkEdit = async (field, value) => {
    if (
      !validateBulkSelection(selected, showSnackbar, setHeaderFilters, field)
    ) {
      return;
    }

    if (
      !validateBulkDates(
        field,
        value,
        selected,
        tableData,
        validateDates,
        showSnackbar,
        setHeaderFilters,
      )
    ) {
      return;
    }

    showConfirmDialog(
      "Confirm Bulk Update",
      `Are you sure you want to update ${selected.length} selected row(s)?`,
      async () => {
        try {
          const response = await performBulkUpdateAPI(
            field,
            value,
            selected,
            formatDateForSQL,
            bulkUpdateJobCodeMappings,
          );

          if (response.data.status) {
            handleSuccessfulBulkUpdate(
              selected,
              field,
              showSnackbar,
              setHighlightedCells,
              getCellKey,
              loadTableData,
              setSelected,
              setHeaderFilters,
            );
          } else {
            showSnackbar(
              response.data.message || "Failed to update records",
              "error",
            );
          }
        } catch (error) {
          console.error("Error in bulk update:", error);
          showSnackbar("Failed to update records", "error");
        }
      },
      "primary",
    );
  };

  // Bulk Delete Function
  const handleBulkDelete = () => {
    if (selected.length === 0) {
      showSnackbar("Please select rows to delete", "warning");
      return;
    }

    const selectedRows = tableData.filter((row) =>
      selected.includes(row.coreRatioCountryJobCodeMapId),
    );

    const message = (
      <Box>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Are you sure you want to delete {selected.length} selected record(s)?
        </Typography>
        <Typography variant="body2" sx={{ ...fontStyles, fontWeight: "bold" }}>
          Records to be deleted:
        </Typography>
        <Box sx={{ maxHeight: "200px", overflow: "auto", mt: 1 }}>
          {selectedRows.slice(0, 10).map((row, index) => (
            <Typography key={index} variant="body2" sx={fontStyles}>
              • {row.program?.name} ({row.program?.code}) - {row.country?.name}{" "}
              - {row.jobCode?.code}
            </Typography>
          ))}
          {selectedRows.length > 10 && (
            <Typography variant="body2" sx={{ ...fontStyles, mt: 1 }}>
              ... and {selectedRows.length - 10} more
            </Typography>
          )}
        </Box>
      </Box>
    );

    showConfirmDialog(
      "Confirm Bulk Delete",
      message,
      async () => {
        try {
          setLoading(true);
          const deletePromises = selected.map((id) => {
            const deletePayload = {
              coreRatioCountryJobCodeMapId: id,
              hardDelete: false,
              deleteDate: null,
            };
            return deleteJobCodeMapping(deletePayload);
          });

          const responses = await Promise.all(deletePromises);

          const successCount = responses.filter(
            (response) => response.data.status,
          ).length;
          const failCount = responses.length - successCount;

          if (successCount > 0) {
            showSnackbar(
              `Successfully deleted ${successCount} record(s)${
                failCount > 0 ? `, ${failCount} failed` : ""
              }`,
              failCount > 0 ? "warning" : "success",
            );
            await loadTableData();
            setSelected([]);
          } else {
            showSnackbar("Failed to delete records", "error");
          }
        } catch (error) {
          console.error("Error in bulk delete:", error);
          showSnackbar("Failed to delete records", "error");
        } finally {
          setLoading(false);
        }
      },
      "error",
    );
  };

  // CRUD Operations
  const handleAdd = () => {
    setEditMode(false);
    setFormData({
      coreRatioCountryJobCodeMapId: null,
      program: null,
      programCode: "",
      country: null,
      countryCode: "",
      jobCode: null,
      jobCodeValue: "",
      department: null,
      departmentCode: "",
      departmentNumber: "",
      coreRatioBucket: null,
      startDate: new Date(),
      endDate: null,
    });
    setOpenDialog(true);
  };

  const handleEdit = (rowData) => {
    setEditMode(true);
    setFormData({
      coreRatioCountryJobCodeMapId: rowData.coreRatioCountryJobCodeMapId,
      program: rowData.program,
      programCode: rowData.program?.code || "",
      country: rowData.country,
      countryCode: rowData.country?.code || "",
      jobCode: rowData.jobCode,
      jobCodeValue: rowData.jobCode?.code || "",
      department: rowData.department,
      departmentCode: rowData.department?.code || "",
      departmentNumber: rowData.department?.number || "",
      coreRatioBucket: rowData.coreRatioBucket,
      startDate: rowData.startDate,
      endDate: rowData.endDate,
    });
    setOpenDialog(true);
    handleMenuClose();
  };

  const handleDelete = async (rowData) => {
    const message = (
      <Box>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Are you sure you want to delete this record?
        </Typography>
        <Typography variant="body2" sx={fontStyles}>
          <strong>Program:</strong> {rowData.program?.name} (
          {rowData.program?.code})
        </Typography>
        <Typography variant="body2" sx={fontStyles}>
          <strong>Country:</strong> {rowData.country?.name}
        </Typography>
        <Typography variant="body2" sx={fontStyles}>
          <strong>Job Code:</strong> {rowData.jobCode?.code}
        </Typography>
      </Box>
    );

    showConfirmDialog(
      "Confirm Delete",
      message,
      async () => {
        try {
          const deletePayload = {
            coreRatioCountryJobCodeMapId: rowData.coreRatioCountryJobCodeMapId,
            hardDelete: false,
            deleteDate: null,
          };

          const response = await deleteJobCodeMapping(deletePayload);

          if (response.data.status) {
            showSnackbar("Record deleted successfully", "success");
            await loadTableData();
          } else {
            showSnackbar(
              response.data.message || "Failed to delete record",
              "error",
            );
          }
        } catch (error) {
          console.error("Error deleting record:", error);
          showSnackbar("Failed to delete record", "error");
        }
      },
      "error",
    );
    handleMenuClose();
  };

  // Helper function to build update payload
  const buildUpdatePayload = (formData) => {
    return {
      coreRatioCountryJobCodeMapId: formData.coreRatioCountryJobCodeMapId,
      programCode: formData.program?.code,
      countryCode: formData.country?.code,
      jobCode: formData.jobCode?.code || null,
      departmentCode: formData.department?.code || null,
      coreRatioBucketId: formData.coreRatioBucket?.id,
      startDate: formatDateForSQL(formData.startDate),
      endDate: formatDateForSQL(formData.endDate),
    };
  };

  // Helper function to build create payload
  const buildCreatePayload = (formData) => {
    return {
      programCode: formData.program?.code,
      countryCode: formData.country?.code,
      jobCode: formData.jobCode?.code || null,
      departmentCode: formData.department?.code || null,
      coreRatioBucketId: formData.coreRatioBucket?.id,
      startDate: formatDateForSQL(formData.startDate),
      endDate: formatDateForSQL(formData.endDate),
    };
  };

  // Helper function to handle save response
  const handleSaveResponse = async (response, successMessage) => {
    if (response.data.status) {
      showSnackbar(successMessage, "success");
      await loadTableData();
      setOpenDialog(false);
    } else {
      showSnackbar(response.data.message || "Failed to save record", "error");
    }
  };

  // Refactored handleSave function
  const handleSave = async () => {
    if (!formData.program || !formData.country || !formData.coreRatioBucket) {
      showSnackbar(
        "Please fill all required fields (Program, Country, Core Ratio Bucket)",
        "warning",
      );
      return;
    }

    const dateError = validateDates(formData.startDate, formData.endDate);
    if (dateError) {
      showSnackbar(dateError, "error");
      return;
    }

    try {
      if (editMode) {
        const updatePayload = buildUpdatePayload(formData);
        const response = await updateJobCodeMapping(updatePayload);
        await handleSaveResponse(response, "Record updated successfully");
      } else {
        const createPayload = buildCreatePayload(formData);
        const response = await createJobCodeMapping(createPayload);
        await handleSaveResponse(response, "Record created successfully");
      }
    } catch (error) {
      console.error("Error saving record:", error);
      showSnackbar("Failed to save record", "error");
    }
  };

  // Menu Handlers
  const handleMenuOpen = (event, record) => {
    setAnchorEl(event.currentTarget);
    setMenuRecord(record);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRecord(null);
  };

  // Toggle inactive records handler
  const handleToggleInactive = (event) => {
    setShowInactive(event.target.checked);
    setSelected([]);
    setPage(0);
  };

  // Extract the table row rendering logic
  const renderTableRow = (row) => {
    const isItemSelected = isSelected(row.coreRatioCountryJobCodeMapId);

    return (
      <TableRow
        key={row.coreRatioCountryJobCodeMapId}
        hover
        selected={isItemSelected}
        onClick={(event) =>
          handleRowClick(event, row.coreRatioCountryJobCodeMapId)
        }
        sx={{
          cursor: "pointer",
          "&.Mui-selected": {
            "& .MuiTableCell-root": {
              color: fontStyles.color,
            },
          },
          "&.Mui-selected:hover": {
            "& .MuiTableCell-root": {
              color: fontStyles.color,
            },
          },
        }}
      >
        <TableCell padding="checkbox">
          <Checkbox
            checked={isItemSelected}
            onChange={() => handleSelectRow(row.coreRatioCountryJobCodeMapId)}
          />
        </TableCell>
        <TableCell align="center">
          <Tooltip title="More actions">
            <IconButton size="small" onClick={(e) => handleMenuOpen(e, row)}>
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        </TableCell>
        <TableCell
          sx={getHighlightedCellStyles(
            row.coreRatioCountryJobCodeMapId,
            "program",
          )}
        >
          {row.program ? `${row.program.name} (${row.program.code})` : ""}
        </TableCell>
        <TableCell sx={fontStyles}>{row.program?.code}</TableCell>
        <TableCell
          sx={getHighlightedCellStyles(
            row.coreRatioCountryJobCodeMapId,
            "startDate",
          )}
        >
          {formatDate(row.startDate)}
        </TableCell>
        <TableCell
          sx={getHighlightedCellStyles(
            row.coreRatioCountryJobCodeMapId,
            "endDate",
          )}
        >
          {formatDate(row.endDate)}
        </TableCell>
        <TableCell
          sx={getHighlightedCellStyles(
            row.coreRatioCountryJobCodeMapId,
            "country",
          )}
        >
          {row.country?.name}
        </TableCell>
        <TableCell sx={fontStyles}>{row.country?.code}</TableCell>
        <TableCell
          sx={getHighlightedCellStyles(
            row.coreRatioCountryJobCodeMapId,
            "jobCode",
          )}
        >
          {row.jobCode?.code}
        </TableCell>
        <TableCell
          sx={getHighlightedCellStyles(
            row.coreRatioCountryJobCodeMapId,
            "department",
          )}
        >
          {row.department?.name}
        </TableCell>
        <TableCell sx={fontStyles}>{row.department?.number}</TableCell>
        <TableCell
          sx={getHighlightedCellStyles(
            row.coreRatioCountryJobCodeMapId,
            "coreRatioBucket",
          )}
        >
          {row.coreRatioBucket?.name}
        </TableCell>
      </TableRow>
    );
  };

  // Extract table body rendering logic
  const renderTableBody = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={12} align="center" sx={fontStyles}>
            Loading...
          </TableCell>
        </TableRow>
      );
    }

    if (paginatedData.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={12} align="center" sx={fontStyles}>
            No data available
          </TableCell>
        </TableRow>
      );
    }

    return paginatedData.map((row) => renderTableRow(row));
  };

  return (
    <>
      {loading && <BackdropComponent />}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box
          sx={{
            mb: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{
                ...headerFontStyles,
                textTransform: "none",
                color: "#FFFFFF",
              }}
            >
              Add New
            </Button>

            {selected.length > 0 && (
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
                sx={{
                  ...headerFontStyles,
                  textTransform: "none",
                  color: "#FFFFFF",
                }}
              >
                Delete Selected
              </Button>
            )}
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={handleToggleInactive}
                color="primary"
              />
            }
            label="Show Inactive Records"
            sx={{
              ...fontStyles,
              "& .MuiFormControlLabel-label": fontStyles,
            }}
          />
        </Box>

        {selected.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Chip
              label={`${selected.length} row(s) selected`}
              color="primary"
              onDelete={() => setSelected([])}
              sx={{
                ...fontStyles,
                color: "#FFFFFF",
                "& .MuiChip-label": {
                  color: "#FFFFFF",
                },
              }}
            />
          </Box>
        )}

        <TableContainer
          sx={{
            maxHeight: `calc(100vh - ${
              selected.length > 0 ? "360px" : "310px"
            })`,
            overflow: "auto",
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={headerFontStyles}>
                  <Checkbox
                    indeterminate={
                      selected.length > 0 && selected.length < tableData.length
                    }
                    checked={
                      tableData.length > 0 &&
                      selected.length === tableData.length
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell
                  align="center"
                  sx={headerFontStyles}
                  style={{ minWidth: "100px" }}
                >
                  Actions
                </TableCell>
                <TableCell sx={headerFontStyles} style={{ minWidth: "250px" }}>
                  <TableSortLabel
                    active={orderBy === "program"}
                    direction={orderBy === "program" ? order : "asc"}
                    onClick={() => handleSort("program")}
                    sx={{
                      ...headerFontStyles,
                      "& .MuiTableSortLabel-icon": {
                        color: `${headerFontStyles.color} !important`,
                      },
                    }}
                  >
                    Program
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={headerFontStyles} style={{ minWidth: "130px" }}>
                  <TableSortLabel
                    active={orderBy === "programCode"}
                    direction={orderBy === "programCode" ? order : "asc"}
                    onClick={() => handleSort("programCode")}
                    sx={{
                      ...headerFontStyles,
                      "& .MuiTableSortLabel-icon": {
                        color: `${headerFontStyles.color} !important`,
                      },
                    }}
                  >
                    Program Code
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={headerFontStyles} style={{ minWidth: "180px" }}>
                  <TableSortLabel
                    active={orderBy === "startDate"}
                    direction={orderBy === "startDate" ? order : "asc"}
                    onClick={() => handleSort("startDate")}
                    sx={{
                      ...headerFontStyles,
                      "& .MuiTableSortLabel-icon": {
                        color: `${headerFontStyles.color} !important`,
                      },
                    }}
                  >
                    Start Date
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={headerFontStyles} style={{ minWidth: "180px" }}>
                  <TableSortLabel
                    active={orderBy === "endDate"}
                    direction={orderBy === "endDate" ? order : "asc"}
                    onClick={() => handleSort("endDate")}
                    sx={{
                      ...headerFontStyles,
                      "& .MuiTableSortLabel-icon": {
                        color: `${headerFontStyles.color} !important`,
                      },
                    }}
                  >
                    End Date
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={headerFontStyles} style={{ minWidth: "220px" }}>
                  <TableSortLabel
                    active={orderBy === "country"}
                    direction={orderBy === "country" ? order : "asc"}
                    onClick={() => handleSort("country")}
                    sx={{
                      ...headerFontStyles,
                      "& .MuiTableSortLabel-icon": {
                        color: `${headerFontStyles.color} !important`,
                      },
                    }}
                  >
                    Country
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={headerFontStyles} style={{ minWidth: "130px" }}>
                  <TableSortLabel
                    active={orderBy === "countryCode"}
                    direction={orderBy === "countryCode" ? order : "asc"}
                    onClick={() => handleSort("countryCode")}
                    sx={{
                      ...headerFontStyles,
                      "& .MuiTableSortLabel-icon": {
                        color: `${headerFontStyles.color} !important`,
                      },
                    }}
                  >
                    Country Code
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={headerFontStyles} style={{ minWidth: "150px" }}>
                  <TableSortLabel
                    active={orderBy === "jobCode"}
                    direction={orderBy === "jobCode" ? order : "asc"}
                    onClick={() => handleSort("jobCode")}
                    sx={{
                      ...headerFontStyles,
                      "& .MuiTableSortLabel-icon": {
                        color: `${headerFontStyles.color} !important`,
                      },
                    }}
                  >
                    Job Code
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={headerFontStyles} style={{ minWidth: "220px" }}>
                  <TableSortLabel
                    active={orderBy === "department"}
                    direction={orderBy === "department" ? order : "asc"}
                    onClick={() => handleSort("department")}
                    sx={{
                      ...headerFontStyles,
                      "& .MuiTableSortLabel-icon": {
                        color: `${headerFontStyles.color} !important`,
                      },
                    }}
                  >
                    Dept. Name Costing
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={headerFontStyles} style={{ minWidth: "140px" }}>
                  <TableSortLabel
                    active={orderBy === "departmentNumber"}
                    direction={orderBy === "departmentNumber" ? order : "asc"}
                    onClick={() => handleSort("departmentNumber")}
                    sx={{
                      ...headerFontStyles,
                      "& .MuiTableSortLabel-icon": {
                        color: `${headerFontStyles.color} !important`,
                      },
                    }}
                  >
                    Dept. Number
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={headerFontStyles} style={{ minWidth: "220px" }}>
                  <TableSortLabel
                    active={orderBy === "coreRatioBucket"}
                    direction={orderBy === "coreRatioBucket" ? order : "asc"}
                    onClick={() => handleSort("coreRatioBucket")}
                    sx={{
                      ...headerFontStyles,
                      "& .MuiTableSortLabel-icon": {
                        color: `${headerFontStyles.color} !important`,
                      },
                    }}
                  >
                    Core Ratio Bucket
                  </TableSortLabel>
                </TableCell>
              </TableRow>

              {/* Bulk Edit Row */}
              <TableRow sx={{ bgcolor: "action.hover" }}>
                <TableCell padding="checkbox" />
                <TableCell />
                <TableCell style={{ minWidth: "250px" }}>
                  <Autocomplete
                    size="small"
                    options={programs}
                    fullWidth
                    getOptionLabel={(option) =>
                      option ? `${option.name} (${option.code})` : ""
                    }
                    value={headerFilters.program}
                    onChange={(e, value) => {
                      setHeaderFilters({
                        ...headerFilters,
                        program: value,
                      });
                      if (value && value.code) applyBulkEdit("program", value); // ✅ Check both value and value.code
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Bulk Edit Program"
                        variant="outlined"
                        sx={{
                          "& .MuiInputBase-input": fontStyles,
                          "& .MuiInputLabel-root": fontStyles,
                        }}
                      />
                    )}
                  />
                </TableCell>
                <TableCell style={{ minWidth: "130px" }} />
                <TableCell style={{ minWidth: "180px" }}>
                  <DatePicker
                    value={headerFilters.startDate}
                    onChange={(value) => {
                      setHeaderFilters({
                        ...headerFilters,
                        startDate: value,
                      });
                      if (value) applyBulkEdit("startDate", value);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Bulk Edit Start Date"
                        fullWidth
                        sx={{
                          "& .MuiInputBase-input": fontStyles,
                          "& .MuiInputLabel-root": fontStyles,
                        }}
                      />
                    )}
                  />
                </TableCell>
                <TableCell style={{ minWidth: "180px" }}>
                  <DatePicker
                    value={headerFilters.endDate}
                    onChange={(value) => {
                      setHeaderFilters({
                        ...headerFilters,
                        endDate: value,
                      });
                      if (value) applyBulkEdit("endDate", value);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Bulk Edit End Date"
                        fullWidth
                        sx={{
                          "& .MuiInputBase-input": fontStyles,
                          "& .MuiInputLabel-root": fontStyles,
                        }}
                      />
                    )}
                  />
                </TableCell>
                <TableCell style={{ minWidth: "220px" }}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={countries}
                    getOptionLabel={(option) => option.name || ""}
                    value={headerFilters.country}
                    onChange={(e, value) => {
                      setHeaderFilters({
                        ...headerFilters,
                        country: value,
                      });
                      if (value && value.code) applyBulkEdit("country", value); // ✅ Check both value and value.code
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Bulk Edit Country"
                        variant="outlined"
                        sx={{
                          "& .MuiInputBase-input": fontStyles,
                          "& .MuiInputLabel-root": fontStyles,
                        }}
                      />
                    )}
                  />
                </TableCell>
                <TableCell style={{ minWidth: "130px" }} />
                <TableCell style={{ minWidth: "150px" }}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={jobCodes}
                    getOptionLabel={(option) =>
                      option ? `${option.code} - ${option.description}` : ""
                    }
                    value={headerFilters.jobCode}
                    onChange={(e, value) => {
                      setHeaderFilters({
                        ...headerFilters,
                        jobCode: value,
                      });
                      if (value && value.code) applyBulkEdit("jobCode", value); // ✅ Check both value and value.code
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Bulk Edit Job Code"
                        variant="outlined"
                        sx={{
                          "& .MuiInputBase-input": fontStyles,
                          "& .MuiInputLabel-root": fontStyles,
                        }}
                      />
                    )}
                  />
                </TableCell>
                <TableCell style={{ minWidth: "220px" }}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={departments}
                    getOptionLabel={(option) => option.name || ""}
                    value={headerFilters.department}
                    onChange={(e, value) => {
                      setHeaderFilters({
                        ...headerFilters,
                        department: value,
                      });
                      if (value && value.code)
                        applyBulkEdit("department", value); // ✅ Check both value and value.code
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Bulk Edit Department"
                        variant="outlined"
                        sx={{
                          "& .MuiInputBase-input": fontStyles,
                          "& .MuiInputLabel-root": fontStyles,
                        }}
                      />
                    )}
                  />
                </TableCell>
                <TableCell style={{ minWidth: "140px" }} />
                <TableCell style={{ minWidth: "220px" }}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={coreRatioBuckets}
                    getOptionLabel={(option) => option.name || ""}
                    value={headerFilters.coreRatioBucket}
                    onChange={(e, value) => {
                      setHeaderFilters({
                        ...headerFilters,
                        coreRatioBucket: value,
                      });
                      if (value && value.id)
                        applyBulkEdit("coreRatioBucket", value); // ✅ Check both value and value.id
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Bulk Edit Core Ratio Bucket"
                        variant="outlined"
                        sx={{
                          "& .MuiInputBase-input": fontStyles,
                          "& .MuiInputLabel-root": fontStyles,
                        }}
                      />
                    )}
                  />
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>{renderTableBody()}</TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={tableData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              fontStyles,
            "& .MuiTablePagination-select": fontStyles,
          }}
        />

        {/* Actions Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={() => handleEdit(menuRecord)}
            sx={{
              ...fontStyles,
              "&:hover": {
                color: fontStyles.color,
              },
            }}
          >
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
          <MenuItem
            onClick={() => handleDelete(menuRecord)}
            sx={{
              ...fontStyles,
              "&:hover": {
                color: fontStyles.color,
              },
            }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>

        {/* Add/Edit Dialog */}
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={headerFontStyles}>
            {editMode
              ? "Edit item for Job Code Mapping"
              : "Add new item for Job Code Mapping"}
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2,
                mt: 2,
              }}
            >
              <Autocomplete
                options={programs}
                getOptionLabel={(option) =>
                  option ? `${option.name} (${option.code})` : ""
                }
                value={formData.program}
                onChange={(e, value) =>
                  setFormData({
                    ...formData,
                    program: value,
                    programCode: value?.code || "",
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Program *"
                    sx={{
                      "& .MuiInputBase-input": fontStyles,
                      "& .MuiInputLabel-root": fontStyles,
                    }}
                  />
                )}
              />

              <TextField
                label="Program Code"
                value={formData.programCode}
                disabled
                fullWidth
                sx={{
                  "& .MuiInputBase-input": fontStyles,
                  "& .MuiInputLabel-root": fontStyles,
                }}
              />

              <DatePicker
                label="Start Date"
                value={formData.startDate}
                onChange={(value) =>
                  setFormData({ ...formData, startDate: value })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    sx={{
                      "& .MuiInputBase-input": fontStyles,
                      "& .MuiInputLabel-root": fontStyles,
                    }}
                  />
                )}
              />

              <DatePicker
                label="End Date"
                value={formData.endDate}
                onChange={(value) =>
                  setFormData({ ...formData, endDate: value })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    sx={{
                      "& .MuiInputBase-input": fontStyles,
                      "& .MuiInputLabel-root": fontStyles,
                    }}
                  />
                )}
              />

              <Autocomplete
                options={countries}
                getOptionLabel={(option) => option.name || ""}
                value={formData.country}
                onChange={(e, value) =>
                  setFormData({
                    ...formData,
                    country: value,
                    countryCode: value?.code || "",
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Country *"
                    sx={{
                      "& .MuiInputBase-input": fontStyles,
                      "& .MuiInputLabel-root": fontStyles,
                    }}
                  />
                )}
              />

              <TextField
                label="Country Code"
                value={formData.countryCode}
                disabled
                fullWidth
                sx={{
                  "& .MuiInputBase-input": fontStyles,
                  "& .MuiInputLabel-root": fontStyles,
                }}
              />

              <Autocomplete
                options={jobCodes}
                getOptionLabel={(option) =>
                  option ? `${option.code} - ${option.description}` : ""
                }
                value={formData.jobCode}
                onChange={(e, value) =>
                  setFormData({
                    ...formData,
                    jobCode: value,
                    jobCodeValue: value?.code || "",
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Job Code"
                    sx={{
                      "& .MuiInputBase-input": fontStyles,
                      "& .MuiInputLabel-root": fontStyles,
                    }}
                  />
                )}
              />

              <Box />

              <Autocomplete
                options={departments}
                getOptionLabel={(option) =>
                  option ? `${option.name} (${option.code})` : ""
                }
                value={formData.department}
                onChange={(e, value) =>
                  setFormData({
                    ...formData,
                    department: value,
                    departmentCode: value?.code || "",
                    departmentNumber: value?.number || "",
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Dept. Name Costing"
                    sx={{
                      "& .MuiInputBase-input": fontStyles,
                      "& .MuiInputLabel-root": fontStyles,
                    }}
                  />
                )}
              />

              <TextField
                label="Dept. Number"
                value={formData.departmentNumber}
                disabled
                fullWidth
                sx={{
                  "& .MuiInputBase-input": fontStyles,
                  "& .MuiInputLabel-root": fontStyles,
                }}
              />

              <Autocomplete
                options={coreRatioBuckets}
                getOptionLabel={(option) => option.name || ""}
                value={formData.coreRatioBucket}
                onChange={(e, value) =>
                  setFormData({ ...formData, coreRatioBucket: value })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Core Ratio Bucket *"
                    sx={{
                      "& .MuiInputBase-input": fontStyles,
                      "& .MuiInputLabel-root": fontStyles,
                    }}
                  />
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setOpenDialog(false)}
              variant="outlined"
              color="primary"
              sx={{ ...fontStyles, textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              color="primary"
              sx={{
                ...headerFontStyles,
                textTransform: "none",
                color: "#FFFFFF",
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmDialog.open}
          onClose={handleConfirmDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={headerFontStyles}>{confirmDialog.title}</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, ...fontStyles }}>
              {typeof confirmDialog.message === "string" ? (
                <Typography>{confirmDialog.message}</Typography>
              ) : (
                confirmDialog.message
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleConfirmDialogClose}
              variant="outlined"
              color="primary"
              sx={{ ...fontStyles, textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDialogAccept}
              variant="contained"
              color={confirmDialog.confirmColor}
              sx={{
                ...headerFontStyles,
                textTransform: "none",
                color: "#FFFFFF",
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        <SnackBar snack={snack} snackReset={snackReset}></SnackBar>
      </LocalizationProvider>
    </>
  );
};

export default JobCodeMapping;
